import { InputLabel, Typography } from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { t } from 'ttag';
import { useStyles } from './styles';
import { Button, useConfirmation } from '@coolremote/shared';
import { Add } from '@material-ui/icons';

import {
  useModbusScheduleForm,
  useModbusSchedules,
} from '../../../../../hooks/useModbusSchedules';
import ScheduleActionRow from './ScheduleActionRow';
import AddActionSelector from './AddActionSelector';
import ScheduleFormFields from './ScheduleFormFields';
import ScheduleWrapper from '@components/Schedules/components/ScheduleWrapper';

const AddEditSchedule = (props) => {
  const classes = useStyles();
  const {
    modbusUnit,
    scheduleData = {},
    onClose,
    isDesktop,
  } = props;

  const confirm = useConfirmation();

  // Use Modbus schedules hook for API operations only - this dialog never
  // reads the schedules list, so skip the hook's own fetch-on-mount.
  const {
    createSchedule,
    updateSchedule,
    error: scheduleError,
  } = useModbusSchedules(modbusUnit?.id, { skipFetch: true });

  // Use the form hook for all form state management
  const {
    // Form data
    scheduleName,
    handleNameChange,
    scheduleDisabled,
    changeScheduleStatus,
    powerOnTime,
    onSetTime,
    days,
    addRemoveDay,
    dates,
    changeDates,
    tab,
    changeTab,
    isPowerMeterUnit,
    dirty,
    errorMsg,
    // Time format utilities
    is12Hours,
    // Actions
    setIsSubmitting,
    setErrorMsg,
    // Object management
    scheduleActions,
    handleObjectValueChange,
    handleDeleteCustomRow,
    // Action-list picker/selection-menu UI state
    writableRegisterOptions,
    handleSelectCustomRow,
    handleNumericClick,
    activePickerId,
    activePickerRef,
    showSelectionMenu,
    openSelectionMenu,
    closeSelectionMenu,
    // Form utilities
    getFormData,
    validateForm,
    resetForm,
  } = useModbusScheduleForm(
    modbusUnit,
    scheduleData
  );

  const editMode = scheduleData?.id;

  // Handle form submission
  const handleSubmit = async () => {
    // A power-meter fallback unit is view/delete-only - defensive guard in
    // case Save is ever reachable another way (the button itself is disabled).
    if (isPowerMeterUnit) {
      return;
    }
    if (!validateForm()) {
      return;
    }

    const formData = getFormData();
    if (!formData) {
      return;
    }

    try {
      setIsSubmitting(true);

      // Determine if this is an update or create operation
      const isUpdate = scheduleData && scheduleData.id;

      // Update existing schedule / Create new schedule
      const savedSchedule = isUpdate
        ? await updateSchedule(scheduleData.id, formData)
        : await createSchedule(formData);

      // Reset form and close dialog
      resetForm();
      if (onClose) {
        onClose(savedSchedule);
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to save schedule';
      setErrorMsg(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (dirty) {
      const result = await confirm({
        title: t`Discard Changes`,
        bodyMessage: t`Are you sure you want to discard changes made on this page?`,
        confirmText: t`Yes`,
        cancelText: t`No`,
        white: true,
        confirmWidth: 120,
        cancelWidth: 120,
      });
      if (result) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  // Combine error messages
  const combinedErrorMsg = errorMsg || scheduleError;

  return (

    <ScheduleWrapper onXClick={handleCancel} isEditMode={editMode} isDesktop={isDesktop}>

      {/* A power-meter fallback unit is view/delete-only - Save is already
          disabled, but these fields would otherwise still look and act
          editable with nothing preventing interaction, which is confusing
          given nothing here can ever be persisted. */}
      <ScheduleFormFields
        scheduleName={scheduleName}
        onNameChange={handleNameChange}
        scheduleDisabled={scheduleDisabled}
        onDisabledChange={changeScheduleStatus}
        tab={tab}
        onTabChange={changeTab}
        days={days}
        onDaysChange={addRemoveDay}
        dates={dates}
        onDatesChange={changeDates}
        powerOnTime={powerOnTime}
        onTimeChange={onSetTime}
        is12Hours={is12Hours}
        isPowerMeterUnit={isPowerMeterUnit}
      />

      <Typography className={clsx(classes.selectModeStyle, classes.selectModeLabel, classes.actionsLabel)}>
        {t`Actions`}
      </Typography>

      {/* Render Schedule Actions */}
      {Object.entries(scheduleActions).map(([key, action]) => (
        <ScheduleActionRow
          key={key}
          object={action}
          activePickerId={activePickerId}
          onValueChange={handleObjectValueChange}
          onNumericClick={handleNumericClick}
          onDelete={handleDeleteCustomRow}
          activePickerRef={activePickerRef}
        />
      ))}
      {!!showSelectionMenu && (
        <AddActionSelector
          options={writableRegisterOptions}
          onSelect={handleSelectCustomRow}
          onClose={closeSelectionMenu}
        />
      )}
      {/* A power-meter fallback unit has nothing controllable to add - its
          rows are monitoring readings, not controls (see isModbusPowerMeterFallback). */}
      {!isPowerMeterUnit && (
        <div
          className={classes.newRowContainer}
          onClick={openSelectionMenu}
        >
          <Add className={classes.addIcon} />
          <Typography className={classes.addText}>{t`Add Controls`}</Typography>
        </div>
      )}
      {/* Error Message */}
      {combinedErrorMsg && (
        <InputLabel className={classes.errorLabelStyle}>
          {combinedErrorMsg}
        </InputLabel>
      )}

      <div className={classes.actionsContainer}>
        <Button
          disableElevation
          disableRipple
          variant="text"
          onClick={handleCancel}
          className={classes.cancelButton}
        >
          {t`Cancel`}
        </Button>
        <Button
          disableElevation
          disableRipple
          type="submit"
          variant="contained"
          onClick={handleSubmit}
          disabled={!dirty || isPowerMeterUnit}
          className={classes.redBtn}
        >
          {t`Save`}
        </Button>
      </div>
    </ScheduleWrapper>
  );
};
export default AddEditSchedule;
